import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PieChart, CheckCircle, Loader2, Calendar, Trash2, CalendarClock, RefreshCw, Banknote, Building, CalendarDays, CreditCard, Euro, FileText, Landmark, ListChecks, Receipt, Repeat, Building2 } from 'lucide-react';
import { formatDate, calculateNextDueDate, getPaymentMethodConfig, getEcheanceStatus } from './constants';
import { AnnulerPaiementButton } from './AnnulerPaiementButton';
import { EcheancesSouscription } from './EcheancesSouscription';
import { PaiementsAnnules } from './PaiementsAnnules';

export default function PartielsTab({
  dashboardStats, calculateMontantTTC, calculateMontantHT: calculateMontantHT,
  downloadPartialReceipt: downloadPartialReceipt, archiveRecuToGED: archiveRecuToGED, archiveAllRecusToGED,
  openRecuEcheanceModal: openRecuEcheanceModal, generateRecuEcheance, generateRecuContributionAcquittee,
  openPartialPaymentModal, openEcheanceModal, openEcheancierModal,
  printEcheancier, handleDeleteEcheancier, handleResetEcheancier,
  openValidationModal,
  archiving, generatingRecu,
  getAuthHeader, fetchData
}) {
  return (
        <div className="space-y-4">
          {dashboardStats.documentsAvecPaiementsPartiels.length === 0 ? (
            <Card className="border">
              <CardContent className="py-12 text-center">
                <PieChart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700">Aucun paiement partiel en cours</h3>
                <p className="text-gray-500 mt-2">Les souscriptions avec des paiements partiels apparaîtront ici</p>
              </CardContent>
            </Card>
          ) : (
            dashboardStats.documentsAvecPaiementsPartiels.map((doc, idx) => {
              const totalTTC = calculateMontantTTC(doc);
              const montantPaye = doc.total_paye || doc.montant_paye || doc.paiements_partiels?.reduce((s, p) => s + (p.amount || p.montant || 0), 0) || 0;
              const reste = totalTTC - montantPaye;
              const progression = totalTTC > 0 ? ((montantPaye / totalTTC) * 100).toFixed(0) : 0;
              const nextDueDate = calculateNextDueDate(doc);
              const echeanceStatus = getEcheanceStatus(nextDueDate);
              const lastPaymentDate = doc.paiements_partiels?.length > 0 
                ? (doc.paiements_partiels[doc.paiements_partiels.length - 1]?.date || doc.paiements_partiels[doc.paiements_partiels.length - 1]?.date_paiement) 
                : null;
              
              return (
                <Card key={idx} className="border shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      {/* Info principale */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-purple-100">
                              <Building2 className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg">{doc.raison_sociale}</h3>
                              <p className="text-sm text-gray-500">{doc.numero_police || doc.id}</p>
                              {doc.siret && <p className="text-xs text-gray-400">SIRET: {doc.siret}</p>}
                            </div>
                          </div>
                          {/* Badge prochaine échéance */}
                          {nextDueDate && (
                            <div className={`px-3 py-1.5 rounded-lg ${echeanceStatus.color} flex items-center gap-2`}>
                              <CalendarClock className="h-4 w-4" />
                              <div className="text-right">
                                <p className="text-xs font-medium">{echeanceStatus.label}</p>
                                <p className="text-xs">{formatDate(nextDueDate)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Dates importantes */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                            <Calendar className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-gray-500">Date création</p>
                              <p className="text-sm font-medium">{formatDate(doc.date_creation || doc.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <div>
                              <p className="text-xs text-gray-500">Dernier paiement</p>
                              <p className="text-sm font-medium">{formatDate(lastPaymentDate)}</p>
                            </div>
                          </div>
                          {/* Prochaine échéance - cliquable pour modifier */}
                          <button 
                            onClick={() => openEcheanceModal(doc)}
                            className={`flex items-center gap-2 p-2 rounded-lg text-left hover:ring-2 hover:ring-offset-1 transition-all ${echeanceStatus.status === 'overdue' ? 'bg-red-50 hover:ring-red-300' : echeanceStatus.status === 'urgent' ? 'bg-amber-50 hover:ring-amber-300' : 'bg-blue-50 hover:ring-blue-300'}`}
                          >
                            <CalendarClock className={`h-4 w-4 ${echeanceStatus.status === 'overdue' ? 'text-red-500' : echeanceStatus.status === 'urgent' ? 'text-amber-500' : 'text-blue-500'}`} />
                            <div>
                              <p className="text-xs text-gray-500">Prochaine échéance ✏️</p>
                              <p className="text-sm font-medium">{formatDate(nextDueDate)}</p>
                            </div>
                          </button>
                          <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                            <ListChecks className="h-4 w-4 text-purple-500" />
                            <div>
                              <p className="text-xs text-gray-500">Nb paiements</p>
                              <p className="text-sm font-medium">{doc.paiements_partiels?.length || 0}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions rapides */}
                        <div className="mt-3 flex gap-2 flex-wrap">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => openEcheanceModal(doc)}
                          >
                            <CalendarClock className="h-3 w-3 mr-1" />
                            Modifier échéance
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs"
                            onClick={() => openEcheancierModal(doc)}
                          >
                            <CalendarDays className="h-3 w-3 mr-1" />
                            Créer échéancier
                          </Button>
                          {/* Bouton Imprimer échéancier */}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => printEcheancier(doc)}
                            disabled={generatingRecu}
                          >
                            {generatingRecu ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <FileText className="h-3 w-3 mr-1" />
                            )}
                            Imprimer échéancier
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                            onClick={() => archiveAllRecusToGED(doc.id)}
                            disabled={archiving}
                          >
                            {archiving ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Building className="h-3 w-3 mr-1" />
                            )}
                            Archiver → GED
                          </Button>
                          {/* Boutons gestion échéancier - Super Admin */}
                          {doc.echeancier && doc.echeancier.length > 0 && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleResetEcheancier(doc)}
                                disabled={!doc.echeancier.some(e => e.paye || e.statut === 'paye')}
                                title={doc.echeancier.some(e => e.paye || e.statut === 'paye') 
                                  ? "Réinitialiser l'échéancier" 
                                  : "Aucune échéance payée"}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Réinit.
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleDeleteEcheancier(doc)}
                                title="Supprimer l'échéancier"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Suppr.
                              </Button>
                            </>
                          )}
                        </div>
                        <EcheancesSouscription doc={doc} getAuthHeader={getAuthHeader} onDone={() => fetchData(true)} />
                        <PaiementsAnnules doc={doc} getAuthHeader={getAuthHeader} onDone={() => fetchData(true)} />
                        
                        {/* Barre de progression */}
                        <div className="mt-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Progression du paiement</span>
                            <span className="font-bold text-purple-600">{progression}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-indigo-600 h-3 rounded-full transition-all"
                              style={{ width: `${progression}%` }}
                            />
                          </div>
                        </div>
                        
                        {/* Montants */}
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div className="p-3 bg-green-50 rounded-lg text-center">
                            <p className="text-xl font-bold text-green-600">{montantPaye.toLocaleString('fr-FR')} €</p>
                            <p className="text-xs text-gray-500">Déjà payé</p>
                          </div>
                          <div className="p-3 bg-red-50 rounded-lg text-center">
                            <p className="text-xl font-bold text-red-600">{reste.toLocaleString('fr-FR')} €</p>
                            <p className="text-xs text-gray-500">Reste à payer</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <p className="text-xl font-bold text-slate-700">{totalTTC.toLocaleString('fr-FR')} €</p>
                            <p className="text-xs text-gray-500">Montant Total</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Historique paiements avec édition de reçus */}
                      <div className="lg:w-96 bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
                          <ListChecks className="h-4 w-4" />
                          Historique des paiements ({doc.paiements_partiels?.length || 0})
                        </h4>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                          {doc.paiements_partiels?.map((paiement, pidx) => {
                            const methodConfig = getPaymentMethodConfig(paiement.method || paiement.mode_paiement);
                            const refContribution = doc.numero_police || doc.id;
                            const numeroRecu = `RCS-ECH-${refContribution}-${String(pidx + 1).padStart(2, '0')}`;
                            const hasRecu = paiement.numero_recu || paiement.recu_genere;
                            
                            return (
                              <div key={pidx} className="p-3 bg-white rounded-lg border text-sm hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs font-bold bg-gradient-to-r from-emerald-600 to-green-600 text-white border-emerald-700">
                                      #{pidx + 1}
                                    </Badge>
                                    <p className="font-bold text-gray-900">{(paiement.amount || paiement.montant || 0).toLocaleString('fr-FR')} €</p>
                                  </div>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${methodConfig.color}`}>
                                    {methodConfig.icon === 'Landmark' && <Landmark className="h-3 w-3" />}
                                    {methodConfig.icon === 'Repeat' && <Repeat className="h-3 w-3" />}
                                    {methodConfig.icon === 'CreditCard' && <CreditCard className="h-3 w-3" />}
                                    {methodConfig.icon === 'FileText' && <FileText className="h-3 w-3" />}
                                    {methodConfig.icon === 'Banknote' && <Banknote className="h-3 w-3" />}
                                    {methodConfig.icon === 'Euro' && <Euro className="h-3 w-3" />}
                                    {methodConfig.shortLabel}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>{formatDate(paiement.date || paiement.date_paiement)}</span>
                                  </div>
                                  {paiement.reference && (
                                    <span className="truncate max-w-[100px]" title={paiement.reference}>
                                      Réf: {paiement.reference}
                                    </span>
                                  )}
                                </div>
                                {/* Bouton édition reçu */}
                                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                  <span className="text-[10px] text-gray-400 font-mono">{numeroRecu}</span>
                                  <div className="flex items-center gap-1">
                                  <AnnulerPaiementButton souscriptionId={doc.id} paiement={paiement}
                                    getAuthHeader={getAuthHeader} onDone={fetchData} />
                                  <Button
                                    size="sm"
                                    variant={hasRecu ? "outline" : "default"}
                                    className={`h-7 text-xs ${hasRecu ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white'}`}
                                    onClick={() => generateRecuEcheance(doc, paiement, pidx)}
                                    disabled={generatingRecu}
                                  >
                                    {generatingRecu ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <Receipt className="h-3 w-3 mr-1" />
                                    )}
                                    {hasRecu ? 'Reçu' : 'Éditer reçu'}
                                  </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Bouton reçu acquitté si soldé */}
                        {reste <= 0.01 ? (
                          <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-emerald-600" />
                                <span className="text-sm font-semibold text-emerald-800">Contribution ACQUITTÉE</span>
                              </div>
                              <Badge className="bg-emerald-600 text-white text-xs font-semibold">Soldé</Badge>
                            </div>
                            <Button
                              size="sm"
                              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                              onClick={() => generateRecuContributionAcquittee(doc)}
                              disabled={generatingRecu}
                            >
                              {generatingRecu ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Receipt className="h-4 w-4 mr-2" />
                              )}
                              Éditer reçu de contribution acquittée
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-2 mt-4">
                            <Button 
                              size="sm" 
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                              onClick={() => openPartialPaymentModal(doc)}
                            >
                              <Banknote className="h-4 w-4 mr-1" />
                              Ajouter
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1"
                              onClick={() => openValidationModal(doc)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Solder
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
  );

}
